export async function waitForBrowserPushWorker(registration: ServiceWorkerRegistration): Promise<void> {
  // During an update active can still be the previous worker, without the audio message handler.
  const worker = registration.installing ?? registration.waiting ?? registration.active;
  if (!worker) throw new Error('Browser push worker missing');
  if (worker.state === 'activated') return;
  await new Promise<void>((resolve, reject): void => {
    const finish = (error?: Error): void => {
      clearTimeout(timer);
      worker.removeEventListener('statechange', changed);
      error ? reject(error) : resolve();
    };
    const changed = (): void => {
      if (worker.state === 'activated') finish();
      else if (worker.state === 'redundant') finish(new Error('Browser push worker installation failed'));
    };
    const timer = setTimeout((): void => finish(new Error('Browser push worker activation timed out')), 5000);
    worker.addEventListener('statechange', changed);
    changed();
  });
}

// Only browser subscription failures are retried, not backend device registration.
export async function getBrowserPushTokenWithRetry(
  getToken: () => Promise<string>,
  signal: AbortSignal,
): Promise<string | null> {
  const delays: readonly number[] = [250, 1000];
  for (let attempt = 0; attempt <= delays.length; attempt++) {
    if (signal.aborted) return null;
    try {
      const token = await getToken();
      return signal.aborted ? null : token;
    } catch (error) {
      if (signal.aborted) return null;
      const name: unknown = error && typeof error === 'object' ? Reflect.get(error, 'name') : null;
      if ((name !== 'AbortError' && name !== 'NetworkError') || attempt === delays.length) throw error;
      if (!await waitForRetry(delays[attempt], signal)) return null;
    }
  }
  return null;
}

function waitForRetry(delay: number, signal: AbortSignal): Promise<boolean> {
  return new Promise((resolve): void => {
    if (signal.aborted) { resolve(false); return; }
    const finish = (ready: boolean): void => {
      clearTimeout(timer);
      signal.removeEventListener('abort', cancel);
      resolve(ready);
    };
    const cancel = (): void => finish(false);
    const timer = setTimeout((): void => finish(true), delay);
    signal.addEventListener('abort', cancel, {once: true});
  });
}
